import React, {useState} from "react";
import {useDispatch} from "react-redux";
import {useHistory} from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import PhoneInput from "react-phone-number-input";
import ru from "react-phone-number-input/locale/ru.json";
import CustomButton from "../../UI/Button";
import EmptyOrShow from "../../EmptyOrShow";
import {setDealCreateData, setShowModal} from "../../../services/redux/actions/app-actions";
import {checkDealByPhone} from "../../../pages/deals/view/api";
import {PAGES} from "../../../constants";
import {getPhoneLength} from "../../../utils";
import {useCustomSnackbar} from "../../../utils/hooks/useCustomSnackbar";
import styleConstants from "../../../constants/styleConstants";

const HEADERS = [
  "ID",
  "Клиент",
  "Статус",
  "Источник",
  "ОП",
  "Хантер",
  "Город",
  "ЖК",
  "Кв.",
  "Создана",
  ""
];

export default function CreateDealPhoneCheckModal() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {addCloseSnack} = useCustomSnackbar();
  const [phone, setPhone] = useState("+7");
  const [deals, setDeals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const digits = (phone || "").replace(/\D/g, "");

  const handlePhoneChange = (value) => {
    // Keep KZ country calling code; don't allow wiping "+7"
    if (!value || value === "+") {
      setPhone("+7");
      return;
    }
    setPhone(value);
  };

  const handleSearch = async () => {
    if (!digits || digits.length < getPhoneLength()) {
      return addCloseSnack("Введите корректный номер телефона");
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await checkDealByPhone(digits);
      setDeals(res?.deals || []);
    } catch (e) {
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const goCreate = () => {
    dispatch(
      setDealCreateData({
        crm_deal_id: null,
        is_access: true,
        phone_number: digits ? `+${digits}` : ""
      })
    );
    dispatch(setShowModal(false));
    history.push(PAGES.dealView);
  };

  const goDeal = (crmDealId) => {
    dispatch(setShowModal(false));
    history.push(`${PAGES.dealView}/${crmDealId}`);
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.hint}>
        Перед созданием сделки проверьте, нет ли уже активного лида по этому номеру.
      </Typography>

      <Box className={classes.phone_row}>
        <PhoneInput
          className={classes.phone_input}
          international
          defaultCountry="KZ"
          country="KZ"
          countryCallingCodeEditable={false}
          labels={ru}
          value={phone || "+7"}
          onChange={handlePhoneChange}
          placeholder="Номер клиента"
        />
        <CustomButton
          btnText="Найти"
          handleClick={handleSearch}
          loading={loading}
          fullWidth={false}
        />
      </Box>

      <EmptyOrShow loading={loading}>
        {searched && deals?.length === 0 ? (
          <Typography className={classes.empty}>
            Активных сделок не найдено. Можно продолжить создание.
          </Typography>
        ) : null}

        {deals?.length > 0 ? (
          <TableContainer className={classes.table_wrap}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {HEADERS.map(h => (
                    <TableCell key={h || "actions"} className={classes.head}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deals.map(deal => (
                  <TableRow key={deal.crm_deal_id} hover>
                    <TableCell>{deal.crm_deal_id}</TableCell>
                    <TableCell>{deal.client_fio || "—"}</TableCell>
                    <TableCell>{deal.crm_status_name || "—"}</TableCell>
                    <TableCell>{deal.origin_name || "—"}</TableCell>
                    <TableCell>{deal.sale_department_name || "—"}</TableCell>
                    <TableCell>{deal.hunter_fio || "—"}</TableCell>
                    <TableCell>{deal.city_name || "—"}</TableCell>
                    <TableCell>{deal.resident_name || "—"}</TableCell>
                    <TableCell>{deal.flat_num || "—"}</TableCell>
                    <TableCell>{deal.created_at || "—"}</TableCell>
                    <TableCell>
                      <CustomButton
                        btnText="Открыть"
                        small
                        fullWidth={false}
                        handleClick={() => goDeal(deal.crm_deal_id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </EmptyOrShow>

      <Box className={classes.actions}>
        <CustomButton
          btnText="Отмена"
          handleClick={() => dispatch(setShowModal(false))}
          className={classes.btn_secondary}
          fullWidth={false}
        />
        <CustomButton
          btnText={deals?.length ? "Всё равно создать" : "Продолжить создание"}
          handleClick={goCreate}
          disabled={!searched || loading}
          fullWidth={false}
        />
      </Box>
    </Box>
  );
}

const useStyles = makeStyles({
  root: {
    minWidth: 720,
    maxWidth: "90vw"
  },
  hint: {
    fontSize: 14,
    color: "#616161",
    marginBottom: 16
  },
  phone_row: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 16
  },
  phone_input: {
    flex: 1,
    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
    height: 45,
    alignItems: "flex-end",
    "& .PhoneInputCountry": {
      alignSelf: "auto",
      height: 18,
      marginBottom: 8
    },
    "& input": {
      border: "none",
      outline: "none",
      background: "none",
      marginBottom: 8,
      fontSize: 16
    }
  },
  empty: {
    fontSize: 14,
    color: "#2E7D32",
    marginBottom: 12
  },
  table_wrap: {
    maxHeight: 360,
    marginBottom: 16,
    border: "1px solid #E0E0E0",
    borderRadius: styleConstants.borderRadius || 4
  },
  head: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    background: "#FAFAFA"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8
  },
  btn_secondary: {
    "& button": {
      backgroundColor: "#9E9E9E !important"
    }
  }
});
